import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/docs/client/x-document-editor.js';
import '@adornis/forms/x-slider.js';
import '@adornis/popover/x-tooltip.js';
import { AsActionbarDropdown } from '@adornis/tiptap/client/components/actionbarElements.js';
import { createCollabExtensionByClass, type VersionData } from '@adornis/tiptap/client/extensions/Collab.js';
import '@adornis/tiptap/client/x-tiptap.js';
import type { XTiptap } from '@adornis/tiptap/client/x-tiptap.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { Observable, combineLatest, debounceTime, distinctUntilChanged, of, switchMap, takeUntil, tap } from 'rxjs';
import * as Y from 'yjs';
import './dla-index.js';

// Tiptap Extensions
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { Spacer } from '@adornis/tiptap/client/extensions/ActionbarSpacer.js';
import { Bold } from '@adornis/tiptap/client/extensions/Bold.js';
import { BulletList } from '@adornis/tiptap/client/extensions/BulletList.js';
import { Color } from '@adornis/tiptap/client/extensions/Color.js';
import { createCommentExtension } from '@adornis/tiptap/client/extensions/Comment/Comment.js';
import '@adornis/tiptap/client/extensions/Comment/x-tiptap-comment-view.js';
import { Document } from '@adornis/tiptap/client/extensions/Document.js';
import { Dropcursor } from '@adornis/tiptap/client/extensions/Dropcursor.js';
import type { AdornisTiptapExtension } from '@adornis/tiptap/client/extensions/ExtensionBase.js';
import { HardBreak } from '@adornis/tiptap/client/extensions/HardBreak.js';
import { Heading } from '@adornis/tiptap/client/extensions/Heading.js';
import { History } from '@adornis/tiptap/client/extensions/History.js';
import { Italic } from '@adornis/tiptap/client/extensions/Italic.js';
import { Link } from '@adornis/tiptap/client/extensions/Link/Link.js';
import { ListItem } from '@adornis/tiptap/client/extensions/ListItem.js';
import { OrderedList } from '@adornis/tiptap/client/extensions/OrderedList.js';
import { Paragraph } from '@adornis/tiptap/client/extensions/Paragraph.js';
import { Strike } from '@adornis/tiptap/client/extensions/Strike.js';
import { SuggestEditExtension } from '@adornis/tiptap/client/extensions/SuggestEdit.js';
import { Tables } from '@adornis/tiptap/client/extensions/Table.js';
import { Text } from '@adornis/tiptap/client/extensions/Text.js';
import { TextAlign } from '@adornis/tiptap/client/extensions/TextAlign.js';
import { TextStyle } from '@adornis/tiptap/client/extensions/TextStyle.js';
import { TransformerHTML } from '@adornis/tiptap/client/extensions/TransformerHTML.js';
import { TransformerJSONObject } from '@adornis/tiptap/client/extensions/TransformerJSONObject.js';
import { TransformerJSONString } from '@adornis/tiptap/client/extensions/TransformerJSONString.js';
import { TransformerMarkdown } from '@adornis/tiptap/client/extensions/TransformerMarkdown.js';
import { Underline } from '@adornis/tiptap/client/extensions/Underline.js';

// Base TipTap Extensions
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { IndentExtension } from '@adornis/tiptap/client/extensions/Indent/Indent.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import Gapcursor from '@tiptap/extension-gapcursor';
import Highlight from '@tiptap/extension-highlight';
import type { DlaTranslationDict } from '../translations.js';

const customHighlight: AdornisTiptapExtension = {
  buttons: ({ editor, host }) => [
    {
      name: 'toggleHighlight',
      icon: 'format_ink_highlighter',
      action: () => editor.chain().focus().toggleHighlight({ color: '#ffc078' }).run(),
      tooltip: host.translator.translate('tiptap', 'highlight', 'tooltipHighlight'),
    },
    {
      name: 'unsetHighlight',
      icon: 'ink_eraser',
      action: () => editor.chain().focus().unsetHighlight().run(),
      tooltip: host.translator.translate('tiptap', 'highlight', 'tooltipHighlightRemove'),
    },
  ],
  extensions: [Highlight],
};

const customGapcursor: AdornisTiptapExtension = {
  extensions: [Gapcursor],
};

const extensions: AdornisTiptapExtension[][] = [
  [
    Document,
    Text,
    TextStyle,
    HardBreak,
    Dropcursor,
    Color,
    ListItem,
    OrderedList,
    Paragraph('text'),
    BulletList,
    customGapcursor,
  ],
  [TransformerHTML, TransformerJSONObject, TransformerJSONString, TransformerMarkdown],
  [Bold, Italic, Underline, Strike, TextAlign],
  [IndentExtension()],
  [Link],
  [AsActionbarDropdown('format_h1', Heading, 'headings')],
  [customHighlight],
  [AsActionbarDropdown('table_chart', Tables, 'tables')],
  [Spacer],
  [History],
];

export interface CollabEditorOptions {
  rid: string;
  username: Maybe<string>;
  userID: Maybe<string>;
  readonly: Maybe<boolean>;
}

@customElement('dla-collab-editor')
export class DLACollabEditor extends ChemistryLitElement {
  @property({ type: Boolean, attribute: 'show-debug-info' }) showDebugInfo = new RXController(this, false);
  @property({ type: Boolean, attribute: 'read-only' }) readOnly = new RXController(this, false);

  @property() options = new RXController<Maybe<CollabEditorOptions>>(this, null);

  private _translator = new TranslationController<DlaTranslationDict>(this);

  private _editorRef: Ref<XTiptap> = createRef();

  private readonly _collab = new RXController(
    this,
    this.options.observable.pipe(
      distinctUntilChanged((prev, cur) => {
        return prev?.rid === cur?.rid;
      }),
      switchMap(async opts => {
        if (!opts || opts.rid.length < 10) {
          return null;
        }
        return createCollabExtensionByClass('document', opts.rid, {
          debugLog: this.showDebugInfo.value,
          enableUndoKeyMap: true,
          userProvider: opts.username || opts.userID ? createUserFromEditorOptions(opts) : undefined,
          readonly: opts.readonly ?? false,
        });
      }),
      tap(collab => {
        if (!collab) {
          return;
        }

        // Trigger re-render if the user or username changes
        combineLatest([collab.subjectUserID.asObservable(), collab.subjectUsername.asObservable()]).subscribe(res => {
          if (!this.showDebugInfo) {
            return;
          }

          this.requestUpdate();
        });

        // Trigger re-render if the collab is ready
        collab.ready.subscribe(() => {
          this.requestUpdate();
        });

        // Trigger re-render if the versions array changes
        collab.doc.getArray('versions').observe(() => this.requestUpdate());

        // Trigger re-render if the user count changes
        collab.awareness.on('update', () => this.requestUpdate());

        // Initial render
        this.requestUpdate();
      }),
    ),
  );

  @state() private readonly _collabError = new RXController(
    this,
    this._collab.observable.pipe(
      switchMap(collab => {
        if (!collab) {
          return of(null);
        }
        return collab.errors.asObservable();
      }),
      tap(val => {
        this.requestUpdate();
      }),
    ),
  );

  private readonly _comments = new RXController(
    this,
    this.options.observable.pipe(
      distinctUntilChanged((prev, cur) => {
        return prev?.rid === cur?.rid;
      }),
      switchMap(async opts => {
        if (!opts || opts.rid.length < 10) {
          return null;
        }
        return createCommentExtension({
          userProvider: opts.username || opts.userID ? createUserFromEditorOptions(opts) : undefined,
          addCommentCallback: () => {
            this._sidebarOpen.next(true);
          },
        });
      }),
    ),
  );

  @state() private _showingSnapshot = new RXController<Maybe<string>>(this, null);
  @state() private _editorChanged = new RXController(this, '');
  @state() private _sidebarOpen = new RXController(this, true);
  @state() private _size = new WindowSizeController(this);

  override connectedCallback(): void {
    super.connectedCallback();

    let lastState: Maybe<string> = null;
    this._editorChanged.observable
      .pipe(
        takeUntil(this.disconnected),
        debounceTime(2500),
        tap(val => {
          if (val === lastState) {
            return;
          }

          if (!lastState) {
            lastState = val;
            return;
          }

          const latestVersion = this._collab.value?.latestVersion();
          if (latestVersion && Date.now() - latestVersion.date < 2500) {
            return;
          }

          lastState = val;
          this._addVersion();
        }),
      )
      .subscribe();
  }

  override updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);

    if (this._editorRef.value) {
      const base = this._editorRef.value.shadowRoot?.querySelector('x-flex') as HTMLElement | null;
      const root = this._editorRef.value.shadowRoot?.querySelector('.tiptap-container') as HTMLElement | null;
      const child = this._editorRef.value.shadowRoot?.querySelector(
        '.tiptap-container > .tiptap',
      ) as HTMLElement | null;

      if (!root || !child || !base) {
        return;
      }

      base.style.minHeight = '400px';
      root.style.minHeight = '400px';
      child.style.minHeight = '400px';
    }
  }

  private _addVersion() {
    this._collab.value?.addVersion();
  }

  private _restoreVersion(version?: Maybe<VersionData>) {
    if (!version && !this._showingSnapshot.value) {
      return;
    }

    version = version ?? this._collab.value?.doc.getArray<VersionData>('versions')?.get(this._selectedVersionIndex());
    if (!version) {
      return;
    }

    if (this._showingSnapshot.value) {
      this._unrenderVersion();
    }
    this._collab.value?.restoreVersion(version);
  }

  private _versionCount() {
    return this._collab.value?.versionCount() ?? 0;
  }

  private _renderVersion(version: any, prevSnapshot?: any) {
    const binding = this._collab.value?.ySyncPluginKey.getState(this._editorRef.value?.getEditor()?.state).binding;
    if (binding != null) {
      binding.renderSnapshot(
        Y.decodeSnapshot(version.snapshot),
        prevSnapshot == null ? Y.emptySnapshot : Y.decodeSnapshot(prevSnapshot),
      );
      this._showingSnapshot.next(version.id);
    }
  }

  private _unrenderVersion() {
    const binding = this._collab.value?.ySyncPluginKey.getState(this._editorRef.value?.getEditor()?.state).binding;
    if (binding != null) {
      binding.unrenderSnapshot();
      this._showingSnapshot.next(null);
    }
  }

  private _debugInfo() {
    if (!this.showDebugInfo.value || !this._collab.value) {
      return nothing;
    }

    return html` <x-flex padding="md" ${css({ backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '5px' })}>
      <x-flex space="sm">
        <x-text>ID: ${this._collab.value.roomID}</x-text>
        <x-text>ClientID: ${this._collab.value.cid}</x-text>
        <x-text>UserID: ${this._collab.value.currentUserID()}</x-text>
        <x-text>Username: ${this._collab.value.currentUsername()}</x-text>
      </x-flex>
    </x-flex>`;
  }

  private _selectedVersionIndex() {
    if (this._showingSnapshot.value === null) {
      return -1;
    }

    const versions = this._collab.value?.doc.getArray('versions') as Y.Array<any>;
    if (!versions) {
      return -1;
    }

    return versions.toArray().findIndex(version => version.id === this._showingSnapshot.value);
  }

  private _versionMove(dx: number) {
    let currentIndex = this._selectedVersionIndex();
    if (currentIndex === -1) {
      currentIndex = this._versionCount();
    }

    const newIndex = currentIndex + dx;
    if (newIndex < 0) {
      return;
    }

    if (newIndex >= this._versionCount()) {
      this._unrenderVersion();
      return;
    }

    const version = this._collab.value?.doc.getArray('versions').get(newIndex);
    if (version) {
      const prevSnapshot =
        newIndex === 0 ? null : this._collab.value?.doc.getArray<VersionData>('versions').get(newIndex - 1).snapshot;
      this._renderVersion(version, prevSnapshot);
    }
  }

  private _versionSlider() {
    const versionCount = this._versionCount();
    if (versionCount === 0) {
      return nothing;
    }

    const currentVersion = !this._showingSnapshot.value
      ? null
      : this._collab.value?.doc.getArray<VersionData>('versions').get(this._selectedVersionIndex());

    return html`<x-flex space="md">
      <x-flex space="sm" crossaxis-center ${css({ flexGrow: 1 })}>
        <x-flex w100 horizontal space-between>
          <x-text ${css({ color: this.colors.neutral })}>
            ${versionCount} ${this._translator.translate('editor', 'versionsRecorded')}
          </x-text>
          <x-text ${css({ color: this.colors.neutral })}>
            ${this._showingSnapshot.value === null ? nothing : this._selectedVersionIndex() + 1}
          </x-text>
          <x-text ${css({ color: this.colors.neutral })}>
            ${this._showingSnapshot.value === null
              ? this._translator.translate('editor', 'current')
              : new Date(currentVersion?.date ?? new Date()).toLocaleString()}
          </x-text>
        </x-flex>
        <x-slider
          ${css({ width: 'calc(100% - 5px)' })}
          .value=${!this._showingSnapshot.value ? versionCount + 1 : this._selectedVersionIndex()}
          min="0"
          max="${versionCount}"
          @value-changed=${(e: ValueEvent<number>) => {
            if (e.detail.value === versionCount) {
              this._unrenderVersion();
            } else {
              const version = this._collab.value?.doc.getArray('versions').get(e.detail.value);
              if (version) {
                const prevSnapshot =
                  e.detail.value === 0
                    ? null
                    : this._collab.value?.doc.getArray<VersionData>('versions').get(e.detail.value - 1).snapshot;
                this._renderVersion(version, prevSnapshot);
              }
            }
            this.requestUpdate();
          }}
        ></x-slider>
      </x-flex>
      <x-flex horizontal space="xs" crossaxis-center>
        <x-button @click=${() => this._versionMove(-1)}>
          <x-icon>
            chevron_left
            <x-tooltip>
              <x-text> ${this._translator.translate('editor', 'versionBack')} </x-text>
            </x-tooltip>
          </x-icon>
        </x-button>
        <x-button @click=${() => this._unrenderVersion()}>
          <x-icon>restart_alt</x-icon>
          <x-tooltip><x-text> ${this._translator.translate('editor', 'versionCurrent')} </x-text></x-tooltip>
        </x-button>
        <x-button @click=${() => this._versionMove(1)}>
          <x-icon>
            chevron_right
            <x-tooltip>
              <x-text> ${this._translator.translate('editor', 'versionAhead')} </x-text>
            </x-tooltip>
          </x-icon>
        </x-button>
        <x-flex ${css({ width: '1px', height: '25px', backgroundColor: 'rgba(0,0,0,0.15)' })}></x-flex>
        <x-button @click=${() => this._restoreVersion()} ?disabled=${!this._showingSnapshot.value}>
          <x-icon>restore</x-icon>
          <x-tooltip><x-text>Restore</x-text></x-tooltip>
        </x-button>
      </x-flex>
    </x-flex>`;
  }

  private _charWordCount() {
    if (!this._collab.value) {
      return nothing;
    }

    let chars = 0;
    let words = 0;

    // recursive counting of characters and words
    let count: (node: any) => void = () => void 0;
    count = (node: any) => {
      if (!node) {
        return;
      }

      if (!node.isLeaf) {
        count(node.forEach(count));
        return;
      }

      if (node.isText) {
        chars += node.text?.length ?? 0;
        words += node.text?.split(' ').filter((s: string) => s.trim().length > 0).length ?? 0;
      }
    };

    this._editorRef.value?.getEditor()?.state.doc.forEach(count);

    return html`<x-flex
      horizontal
      crossaxis-center
      padding="xs"
      space="sm"
      ${css({
        position: 'absolute',
        backgroundColor: 'rgba(0,0,0,0.1)',
        left: this.spacing.sm,
        bottom: this.spacing.sm,
        borderRadius: '5px',
      })}
    >
      <x-text> ${chars} ${this._translator.translate('editor', 'characters')} </x-text>
      <x-text ${css({ color: this.colors.neutral })}>•</x-text>
      <x-text>${words} ${this._translator.translate('editor', 'words')} </x-text>
    </x-flex>`;
  }

  private _connectedUsers() {
    if (!this._collab.value) {
      return nothing;
    }

    return html` <x-flex
      padding="xs"
      ${css({
        position: 'absolute',
        backgroundColor: 'rgba(0,0,0,0.1)',
        right: this.spacing.md,
        bottom: this.spacing.sm,
        borderRadius: '5px',
      })}
    >
      <x-text
        >${this._collab.value.getConnectedUserCount()} ${this._translator.translate('editor', 'connected')}
      </x-text>
      <x-tooltip>
        <x-flex
          horizontal
          space="sm"
          wrap
          ${css({
            maxWidth: '400px',
          })}
        >
          ${this._collab.value.getConnectedUsernames().map(u => {
            return html`<x-flex
              padding="xs"
              ${css({
                backgroundColor: u.color ?? 'rgba(0,0,0,0.1)',
                borderRadius: '5px',
              })}
            >
              <x-text>${u.username} </x-text>
            </x-flex>`;
          })}
        </x-flex>
      </x-tooltip>
    </x-flex>`;
  }

  private _sidebar() {
    if (!this._collab.value || !this._comments.value || this.options.value?.readonly) {
      return nothing;
    }

    const width = `calc(min(450px, ${this._size.width ?? 450}px) - ${this.spacing.md} * 2)`;
    const topOffset = (this._size.width ?? 1000) < 800 ? '80px' : '60px';

    if (!this._sidebarOpen.value) {
      return html`
        <x-flex
          crossaxis-center
          ${css({
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '5px',
            flexGrow: 1,
            zIndex: 999,
            right: this.spacing.md,
            top: topOffset,
            position: 'absolute',
            width: '40px',
            height: '40px',
            backgroundColor: 'rgb(253, 253, 253)',
            overflow: 'auto',
            justifyContent: 'center',
          })}
        >
          <x-tooltip> <x-text> ${this._translator.translate('openSidebar')} </x-text> </x-tooltip>
          <x-button
            mode="icon"
            icon="menu"
            @click=${() => {
              this._sidebarOpen.next(true);
            }}
          ></x-button>
        </x-flex>
      `;
    }

    return html` <style>
        @-webkit-keyframes slide {
          100% {
            right: 0;
          }
        }

        @keyframes slide {
          100% {
            right: 0;
          }
        }
      </style>
      <x-flex
        ${css({
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: '5px',
          flexGrow: 1,
          zIndex: 999,
          right: '-600px',
          WebkitAnimation: 'slide 0.5s forwards',
          animation: 'slide 0.5s forwards',
          top: `${topOffset}`,
          position: 'absolute',
          minWidth: width,
          maxWidth: width,
          height: `calc(100% - ${topOffset} * 2)`,
          backgroundColor: 'rgb(253, 253, 253)',
          boxShadow: `0px 0px 8px 0px ${this.colors.neutral}`,
        })}
      >
        <x-flex
          horizontal
          space-between
          crossaxis-center
          ${css({
            backgroundColor: 'white',
            padding: `${this.spacing.sm} ${this.spacing.md}`,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          })}
        >
          <x-h4>Sidebar</x-h4>
          <x-button
            mode="icon"
            icon="close"
            @click=${() => {
              this._sidebarOpen.next(false);
            }}
          ></x-button>
        </x-flex>
        <x-flex space="xl" padding="md" ${css({ overflow: 'auto' })}>
          ${this._debugInfo()} ${this._versionSlider()}
          <x-flex space="md">
            <x-h4>Index</x-h4>
            <x-hr></x-hr>
            <dla-index
              .editorState=${this._editorRef.value?.getEditor()}
              .onChange=${this._editorChanged.observable}
              @request-scroll=${(e: ValueEvent<number>) => {
                if (!this._editorRef.value) {
                  return;
                }
                this._editorRef.value.scrollTo({
                  top: e.detail.value + this._editorRef.value.scrollTop - 150,
                  behavior: 'smooth',
                });
              }}
            ></dla-index>
          </x-flex>
          <x-flex space="md">
            <x-h4> ${this._translator.translate('editor', 'comments')} </x-h4>
            <x-hr></x-hr>
            <x-tiptap-comment-view .commentState=${this._comments.value.state}></x-tiptap-comment-view>
          </x-flex>
          <slot name="afterComments"></slot>
        </x-flex>
      </x-flex>`;
  }

  override render() {
    if (!this._collab.value || !this._comments.value) {
      return html`<x-flex padding="lg" crossaxis-center><x-loader></x-loader></x-flex>`;
    }

    return html`
      <x-flex space="md" h100 ${css({ position: 'relative' })}>
        ${this._collabError.value
          ? html`
              <x-flex
                ${css({
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  justifyContent: 'center',
                  zIndex: 1000,
                  color: 'white',
                })}
                space="lg"
                crossaxis-center
              >
                <x-icon ${css({ fontSize: '3rem' })}>warning</x-icon>
                <x-text ${css({ fontSize: '1.2rem', fontFamily: 'monospace' })}>${this._collabError.value}</x-text>
                <x-text>Please try reloading the page</x-text>
              </x-flex>
            `
          : nothing}
        <x-flex horizontal h100 space="md" ${css({ flexGrow: 1 })}>
          <x-flex
            ${css({
              flexGrow: 1,
              // paddingBottom: '50px',
            })}
          >
            <x-document-editor
              ${ref(this._editorRef)}
              plain
              .extensions=${[
                ...extensions,
                this._collab.value.extension,
                [SuggestEditExtension(this._collab.value.currentUserID(), this._collab.value.currentUsername())],
                [this._comments.value.extension],
              ]}
              ?read-only=${this.options.value?.readonly || this._collab.value?.ready?.value === false}
              @value-picked=${(e: ValueEvent<string>) => this._editorChanged.next(e.detail.value)}
            ></x-document-editor>
            ${[this._charWordCount(), this._connectedUsers()]}
          </x-flex>
        </x-flex>
        ${this._sidebar()}
      </x-flex>
    `;
  }
}

function createUserFromEditorOptions(
  opts: CollabEditorOptions,
): Observable<string | { userID: string; username: string }> | undefined {
  return new Observable<string | { username: string; userID: string }>(sub => {
    if (opts.username && opts.userID) {
      sub.next({ username: opts.username, userID: opts.userID });
    } else if (opts.username) {
      sub.next(opts.username);
    }
  });
}
